import { type ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import { _any } from "i18n";
import Modal from "ui-kit/src/Modal";
import type {
	EventListTeamInfo,
	GPGamePlayerResponse,
	MergedGpEventResponse,
} from "../../../../types";
import { calculateEventTime } from "../../../../functions";
import { useFullscreenModalContainer } from "../../../../useFullscreenModalContainer";
import type { ReportEventErrorHandler } from "../../../../report-error";
import type { ReportErrorFormValues } from "../../../../report-error/report-error.types";
import { ReportErrorDetailsCard } from "./ReportErrorDetailsCard";
import { ReportErrorSelect } from "./ReportErrorSelect";
import {
	ERROR_TYPE_OPTIONS,
	REPORT_ERROR_LABELS,
	REPORT_EVENT_GROUPS,
	TYPE_CHIP_COLORS,
} from "./reportError.constants";
import type {
	ReportErrorSelectOption,
	WrongEventOption,
} from "./reportError.types";
import {
	buildWrongEventOptions,
	formatEventTypeLabel,
	getActiveEventFromWrongEvent,
	getDefaultWrongEventKey,
	getPlayerForEvent,
	getReportEventOption,
	getWrongEventOption,
} from "./reportError.utils";
import "./ReportErrorModal.css";

interface ReportErrorModalProps {
	show: boolean;
	close: () => void;
	selectedEvent: MergedGpEventResponse | null;
	selectedEvents: MergedGpEventResponse[];
	selectedPlayer: GPGamePlayerResponse | null;
	players?: GPGamePlayerResponse[];
	teamInfo: EventListTeamInfo;
	onSubmit?: ReportEventErrorHandler;
}

function mapToPlayerOptions(
	players: GPGamePlayerResponse[] | undefined,
	selectedClubId: number | null,
	unknownPlayerFallback: string,
): ReportErrorSelectOption<number>[] {
	if (players === undefined || players.length === 0) {
		return [];
	}

	return players
		.filter(
			(player): player is GPGamePlayerResponse & { id: number } =>
				player.id !== null,
		)
		.filter((player) =>
			selectedClubId !== null ? player.teamId === selectedClubId : true,
		)
		.slice()
		.sort((left, right) => (left.name ?? "").localeCompare(right.name ?? ""))
		.map((player) => ({
			value: player.id,
			label: player.name ?? unknownPlayerFallback,
		}));
}

export function ReportErrorModal({
	show,
	close,
	selectedEvent,
	selectedEvents,
	selectedPlayer,
	players,
	teamInfo,
	onSubmit,
}: ReportErrorModalProps) {
	const fullscreenModalContainer = useFullscreenModalContainer();
	const labels = Object.fromEntries(
		Object.entries(REPORT_ERROR_LABELS).map(([key, value]) => [
			key,
			_any(value),
		]),
	) as Record<keyof typeof REPORT_ERROR_LABELS, string>;
	const [errorType, setErrorType] = useState<
		ReportErrorFormValues["errorType"] | ""
	>("");
	const [description, setDescription] = useState("");
	const [selectedClubId, setSelectedClubId] = useState<number | null>(null);
	const [correctPlayerId, setCorrectPlayerId] = useState<number | null>(null);
	const [wrongEventKey, setWrongEventKey] = useState("");
	const [correctEventType, setCorrectEventType] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isSubmitSuccessful, setIsSubmitSuccessful] = useState(false);
	const wasOpenRef = useRef(false);

	const clubOptions = useMemo<ReportErrorSelectOption<number>[]>(() => {
		const clubs: ReportErrorSelectOption<number>[] = [];

		if (teamInfo.homeTeam.id !== null) {
			clubs.push({
				value: teamInfo.homeTeam.id,
				label: teamInfo.homeTeam.clubName ?? labels.homeTeamFallback,
			});
		}

		if (
			teamInfo.awayTeam.id !== null &&
			!clubs.some((club) => club.value === teamInfo.awayTeam.id)
		) {
			clubs.push({
				value: teamInfo.awayTeam.id,
				label: teamInfo.awayTeam.clubName ?? labels.awayTeamFallback,
			});
		}

		return clubs;
	}, [
		labels.awayTeamFallback,
		labels.homeTeamFallback,
		teamInfo.awayTeam.clubName,
		teamInfo.awayTeam.id,
		teamInfo.homeTeam.clubName,
		teamInfo.homeTeam.id,
	]);

	const playerOptions = useMemo<ReportErrorSelectOption<number>[]>(
		() =>
			mapToPlayerOptions(
				players,
				selectedClubId,
				labels.unknownPlayerFallback,
			),
		[labels.unknownPlayerFallback, players, selectedClubId],
	);

	const errorTypeOptions = ERROR_TYPE_OPTIONS.map((option) => ({
		...option,
		label: _any(option.labelKey),
	}));

	const selectedErrorTypeOption = useMemo(
		() => errorTypeOptions.find((option) => option.value === errorType) ?? null,
		[errorType, errorTypeOptions],
	);
	const selectedClubOption = useMemo<ReportErrorSelectOption<number> | null>(
		() =>
			clubOptions.find(
				(club: ReportErrorSelectOption<number>) =>
					club.value === selectedClubId,
			) ?? null,
		[clubOptions, selectedClubId],
	);
	const selectedCorrectPlayerOption =
		useMemo<ReportErrorSelectOption<number> | null>(
			() =>
				playerOptions.find(
					(player: ReportErrorSelectOption<number>) =>
						player.value === correctPlayerId,
				) ?? null,
			[playerOptions, correctPlayerId],
		);
	const selectedCorrectPlayerName = useMemo(
		() => selectedCorrectPlayerOption?.label ?? "",
		[selectedCorrectPlayerOption],
	);
	const getEventTypeLabel = (eventType: string) => {
		const option = getReportEventOption(eventType);
		return option ? _any(option.labelKey) : formatEventTypeLabel(eventType);
	};

	const isWrongPlayerSelected = errorType === "wrong_player";
	const isWrongEventTypeSelected = errorType === "wrong_event_type";
	const wrongEventOptions = useMemo(
		() =>
			buildWrongEventOptions(selectedEvent, selectedEvents).map((option) => ({
				...option,
				label: getEventTypeLabel(option.eventType),
			})),
		[getEventTypeLabel, selectedEvent, selectedEvents],
	);
	const shouldShowWrongEventFields = isWrongEventTypeSelected;
	const defaultWrongEventKey = useMemo(
		() => getDefaultWrongEventKey(wrongEventOptions, selectedEvent),
		[selectedEvent, wrongEventOptions],
	);
	const selectedWrongEventOption = useMemo(
		() => getWrongEventOption(wrongEventOptions, wrongEventKey),
		[wrongEventKey, wrongEventOptions],
	);
	const correctEventOptions = REPORT_EVENT_GROUPS.map((group) => ({
		label: _any(group.labelKey),
		options: group.options.map(
			(option): ReportErrorSelectOption<string> => ({
				value: option.value,
				label: _any(option.labelKey),
			}),
		),
	}));
	const selectedCorrectEventOption = (() => {
		const option = getReportEventOption(correctEventType);
		return option
			? { value: option.value, label: _any(option.labelKey) }
			: null;
	})();
	const activeEvent = useMemo(
		() =>
			getActiveEventFromWrongEvent(
				selectedEvent,
				selectedEvents,
				selectedWrongEventOption,
			),
		[selectedEvent, selectedEvents, selectedWrongEventOption],
	);
	const activePlayer = useMemo(
		() => getPlayerForEvent(activeEvent, players) ?? selectedPlayer,
		[activeEvent, players, selectedPlayer],
	);
	const selectedTeamId = useMemo(() => {
		if (activePlayer?.teamId !== null && activePlayer?.teamId !== undefined) {
			return activePlayer.teamId;
		}

		return activeEvent?.gameTeamId ?? null;
	}, [activeEvent?.gameTeamId, activePlayer?.teamId]);
	const activeClubName = useMemo(() => {
		if (activePlayer?.teamId === teamInfo.homeTeam.id) {
			return teamInfo.homeTeam.clubName ?? "";
		}

		if (activePlayer?.teamId === teamInfo.awayTeam.id) {
			return teamInfo.awayTeam.clubName ?? "";
		}

		if (activeEvent?.gameTeamId === teamInfo.homeTeam.id) {
			return teamInfo.homeTeam.clubName ?? "";
		}

		if (activeEvent?.gameTeamId === teamInfo.awayTeam.id) {
			return teamInfo.awayTeam.clubName ?? "";
		}

		return "";
	}, [
		activeEvent?.gameTeamId,
		activePlayer?.teamId,
		teamInfo.awayTeam.clubName,
		teamInfo.awayTeam.id,
		teamInfo.homeTeam.clubName,
		teamInfo.homeTeam.id,
	]);
	const selectedClubName = useMemo(
		() => selectedClubOption?.label ?? "",
		[selectedClubOption],
	);
	const isSubmitDisabled =
		errorType === "" ||
		(isWrongPlayerSelected &&
			(selectedClubId === null || correctPlayerId === null)) ||
		(shouldShowWrongEventFields &&
			(!selectedWrongEventOption || correctEventType === ""));

	useEffect(() => {
		if (show && !wasOpenRef.current) {
			setErrorType("");
			setDescription("");
			setSelectedClubId(selectedTeamId);
			setCorrectPlayerId(null);
			setWrongEventKey(defaultWrongEventKey);
			setCorrectEventType("");
			setIsSubmitting(false);
			setIsSubmitSuccessful(false);
		}

		if (!show && wasOpenRef.current) {
			setErrorType("");
			setDescription("");
			setSelectedClubId(null);
			setCorrectPlayerId(null);
			setWrongEventKey("");
			setCorrectEventType("");
			setIsSubmitting(false);
			setIsSubmitSuccessful(false);
		}

		wasOpenRef.current = show;
	}, [defaultWrongEventKey, selectedTeamId, show]);

	useEffect(() => {
		if (
			correctPlayerId !== null &&
			!playerOptions.some(
				(player: ReportErrorSelectOption<number>) =>
					player.value === correctPlayerId,
			)
		) {
			setCorrectPlayerId(null);
		}
	}, [correctPlayerId, playerOptions]);

	useEffect(() => {
		if (!show) {
			return;
		}

		if (
			wrongEventKey !== "" &&
			!wrongEventOptions.some(
				(option: WrongEventOption) => option.key === wrongEventKey,
			)
		) {
			setWrongEventKey(defaultWrongEventKey);
		}
	}, [defaultWrongEventKey, show, wrongEventKey, wrongEventOptions]);

	if (!activeEvent) {
		return null;
	}

	const durationSeconds = Math.max(
		Math.round((activeEvent.endTime - activeEvent.startTime) / 1000),
		0,
	);
	const playerName = activePlayer?.name ?? activeEvent.playerName ?? "";
	const playerNumber = activePlayer?.numberSign ?? activeEvent.backNumber ?? "";

	const handleClose = () => {
		if (isSubmitting) {
			return;
		}

		close();
	};

	const handleSubmit = async () => {
		if (
			errorType === "" ||
			(isWrongPlayerSelected &&
				(selectedClubId === null || correctPlayerId === null)) ||
			(shouldShowWrongEventFields &&
				(!selectedWrongEventOption || correctEventType === ""))
		) {
			return;
		}

		if (!onSubmit) {
			return;
		}

		try {
			setIsSubmitting(true);

			await onSubmit(
				{
					errorType,
					description: description.trim(),
					selectedEventIds: selectedEvents.map((event) => event.id),
					selectedClubId: isWrongPlayerSelected ? selectedClubId : undefined,
					selectedClubName: isWrongPlayerSelected
						? selectedClubName
						: undefined,
					correctPlayerId: isWrongPlayerSelected ? correctPlayerId : undefined,
					correctPlayerName: isWrongPlayerSelected
						? selectedCorrectPlayerName
						: undefined,
					wrongEventType: shouldShowWrongEventFields
						? selectedWrongEventOption?.eventType
						: undefined,
					wrongEventLabel: shouldShowWrongEventFields
						? selectedWrongEventOption?.label
						: undefined,
					correctEventType: shouldShowWrongEventFields
						? correctEventType
						: undefined,
					correctEventLabel: shouldShowWrongEventFields
						? selectedCorrectEventOption?.label
						: undefined,
				},
				activeEvent,
			);

			setIsSubmitSuccessful(true);
		} catch {
			// The page-level submit handler surfaces the user-facing error toast.
		} finally {
			setIsSubmitting(false);
		}
	};

	const renderWrongEventLabel = (option: WrongEventOption) => (
		<span className="reportErrorModal__chipOption">
			<span
				className={`reportErrorModal__chipOptionChip ${option.chipColorClassName ?? ""
					}`.trim()}
			>
				#{option.label}
			</span>
		</span>
	);

	return (
		<Modal
			show={show}
			onClose={handleClose}
			title={labels.title}
			size="xxl"
			UNSTABLE_portalContainer={fullscreenModalContainer}
			className="reportErrorModalDialog"
		>
			<div className="reportErrorModal">
				{isSubmitSuccessful ? (
					<div className="reportErrorModal__success">
						<div className="reportErrorModal__successIcon" aria-hidden="true">
							<span className="reportErrorModal__successShield" />
						</div>
						<div className="reportErrorModal__successContent">
							<h2 className="reportErrorModal__successTitle">
								{labels.successTitle}
							</h2>
							<p className="reportErrorModal__successDescription">
								{labels.successDescription}
							</p>
						</div>
						<div className="reportErrorModal__actions reportErrorModal__actions--success">
							<button
								type="button"
								className="reportErrorModal__button reportErrorModal__button--primary"
								onClick={handleClose}
							>
								{labels.okay}
							</button>
						</div>
					</div>
				) : (
					<>
						<div className="reportErrorModal__detailsGrid">
							<ReportErrorDetailsCard
								title={labels.eventDetails}
								iconClassName="icon-new icon-clock icon-16 reportErrorModal__sectionIcon"
								rows={[
									{
										label: labels.time,
										value: calculateEventTime(activeEvent),
									},
									{
										label: labels.duration,
										value: _any("reportError.durationSeconds", {
											count: String(durationSeconds),
										}),
									},
									{
										label: labels.type,
										rowClassName: "reportErrorModal__detailRow--types",
										value: (
											<div className="reportErrorModal__typeList">
												{activeEvent.types.map(
													(type: string, index: number) => (
														<span
															key={`${activeEvent.id}-${type}-${index}`}
															className={`reportErrorModal__typeChip ${TYPE_CHIP_COLORS[
																index % TYPE_CHIP_COLORS.length
															]
																}`}
														>
															#{getEventTypeLabel(type)}
														</span>
													),
												)}
											</div>
										),
									},
								]}
							/>

							<ReportErrorDetailsCard
								title={labels.playerDetails}
								iconClassName="icon-new icon-person icon-16 reportErrorModal__sectionIcon"
								rows={[
									{ label: labels.name, value: playerName },
									{ label: labels.number, value: playerNumber },
									{ label: labels.club, value: activeClubName },
								]}
							/>
						</div>

						<div className="reportErrorModal__form">
							<ReportErrorSelect
								label={labels.errorType}
								required
								options={errorTypeOptions}
								value={selectedErrorTypeOption}
								onChange={(option) => setErrorType(option?.value ?? "")}
								placeholder={labels.choose}
								noOptionsMessage={labels.noOptionsAvailable}
								menuPortalTarget={null}
								isDisabled={isSubmitting}
							/>

							{isWrongPlayerSelected && (
								<div className="reportErrorModal__inlineFields">
									<ReportErrorSelect<ReportErrorSelectOption<number>, number>
										label={labels.selectClub}
										required
										options={clubOptions}
										value={selectedClubOption}
										onChange={(option) => {
											setSelectedClubId(option?.value ?? null);
											setCorrectPlayerId(null);
										}}
										placeholder={labels.choose}
										noOptionsMessage={labels.noOptionsAvailable}
										menuPortalTarget={null}
										isDisabled={isSubmitting}
									/>

									<ReportErrorSelect<ReportErrorSelectOption<number>, number>
										label={labels.selectCorrectPlayer}
										required
										options={playerOptions}
										value={selectedCorrectPlayerOption}
										onChange={(option) =>
											setCorrectPlayerId(option?.value ?? null)
										}
										placeholder={labels.choose}
										isDisabled={isSubmitting || playerOptions.length === 0}
										isSearchable
										noOptionsMessage={labels.noPlayersAvailable}
										menuPortalTarget={null}
									/>
								</div>
							)}

							{shouldShowWrongEventFields && (
								<div className="reportErrorModal__inlineFields">
									<ReportErrorSelect<WrongEventOption>
										label={labels.selectWrongEvent}
										required
										options={wrongEventOptions}
										value={selectedWrongEventOption}
										onChange={(option) => setWrongEventKey(option?.key ?? "")}
										placeholder={labels.choose}
										noOptionsMessage={labels.noWrongEventsAvailable}
										formatOptionLabel={(option) =>
											renderWrongEventLabel(option)
										}
										menuPortalTarget={null}
										isDisabled={isSubmitting}
									/>

									<ReportErrorSelect<ReportErrorSelectOption<string>>
										label={labels.correctEvent}
										fieldClassName="reportErrorModal__field--correctEvent"
										variant="correctEvent"
										required
										options={correctEventOptions}
										value={selectedCorrectEventOption}
										onChange={(option) =>
											setCorrectEventType(option?.value ?? "")
										}
										placeholder={labels.choose}
										isSearchable
										noOptionsMessage={labels.noEventTypesAvailable}
										menuHeaderLabel={labels.resetAll}
										menuPlacement="bottom"
										menuPortalTarget={null}
										isDisabled={isSubmitting}
									/>
								</div>
							)}

							<label className="reportErrorModal__field">
								<span className="reportErrorModal__fieldLabel">
									{labels.description}
								</span>
								<textarea
									className="reportErrorModal__textarea"
									placeholder={labels.descriptionPlaceholder}
									value={description}
									onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
										setDescription(event.target.value)
									}
									disabled={isSubmitting}
								/>
							</label>
						</div>

						<div className="reportErrorModal__actions">
							<button
								type="button"
								className="reportErrorModal__button reportErrorModal__button--secondary"
								onClick={handleClose}
								disabled={isSubmitting}
							>
								{labels.cancel}
							</button>
							<button
								type="button"
								className="reportErrorModal__button reportErrorModal__button--primary"
								onClick={handleSubmit}
								disabled={isSubmitDisabled || isSubmitting}
							>
								{isSubmitting ? labels.sending : labels.send}
							</button>
						</div>
					</>
				)}
			</div>
		</Modal>
	);
}
